import React, { useRef, useState, useContext, useEffect } from "react";
import { EXCLUDED_ATTRIBUTES, HEADER_HEIGHT, STAGE_DMP2 } from "../../constants";
import { FaRegCircleUser } from "react-icons/fa6";
import yt_logo_white from "../../images/TNC_LOGO_WHITE.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { useLayerContext } from "../../context/MasterplanLayerContext";
import { MobileContext } from "../../context/MobileContext";
import { RxHamburgerMenu } from "react-icons/rx";
import { ImCross } from "react-icons/im";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import ReactDOM from "react-dom";
import useAuthStatus from "../../hooks/FirebaseAuth";
import { getReadableName } from "../../pages/Masterplan/GeoSpatialUtils";

interface NavItemType {
  id: string;
  name: string;
  private?: boolean;
  isDropdown?: boolean;
  dropdownItems?: NavItemType[];
  action?: () => void;
}

/**
 * A component that renders the header of the application.
 * The header includes navigation items, user profile, and handles navigation.
 *
 * @param {Object} props - The component props.
 * @param {boolean} [props.minimalHeader=false] - Whether to render a minimal header without navigation items.
 *
 * @returns {JSX.Element} The rendered Header component.
 */
const Header: React.FC<{ minimalHeader?: boolean }> = ({
  minimalHeader = false,
}) => {
  const [currentPage, setCurrentPage] = useState<string>("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [mobileMenuView, setMobileMenuView] = useState<NavItemType[] | null>(
    null
  );
  const [hoverMessage, setHoverMessage] = useState("");
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useContext(MobileContext);

  // Context to change feature layer
  const { allLayers, setLayers, setLayerStyle, layerStage } = useLayerContext();
  const { userRole, checkingStatus } = useAuthStatus();

  /** Logs out user */
  const logout = () => {
    setCurrentPage("");
    auth.signOut();
    navigate("/");
  };

  useEffect(() => {
    const extractPageName = (path: string) => {
      const page = path.split("/")[1];
      return page ? page : "home";
    };

    setCurrentPage(extractPageName(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((authUser) => {
      if (authUser) {
        // Fetch user's first name and last name from Firestore
        const userRef = doc(db, "users", authUser.uid);
        getDoc(userRef)
          .then((doc) => {
            if (doc.exists()) {
              const userData = doc.data();
              setHoverMessage(`Hi ${userData.firstName} ${userData.lastName}`);
            }
          })
          .catch((error) => {
            console.error("Error fetching user data:", error);
          });
      } else {
        // User is signed out
        setHoverMessage("");
      }
    });
    return () => unsubscribe();
  }, []);

  /** Navigates to new page */
  const setPage = (item: string, state?: any) => {
    // setCurrentPage(item);
    navigate(`/${item}`, { state: state });
  };

  /** Called when masterplan item is selected. */
  const navigateToLayer = (
    layer: any,
    style: string | null = null,
    styleType: "discrete" | "continuous" = "discrete"
  ) => {
    // Update layer context if specified, otherwise enable all layers
    if (layer) {
      setLayerStyle(layer?.name, style, styleType);
      setLayers([layer]);
    } else {
      // Reset layers to default
      allLayers?.forEach((layer) => {
        setLayerStyle(layer?.name, style, styleType);
      });
      setLayers(allLayers);
    }

    if (currentPage !== "masterplan") {
      setPage("masterplan");
    }
  };

  /** Navigate to layers belonging to a specific category */
  const navigateToCategoryLayers = (category: string, layers?: LayerType[]) => {
    if (layers) {
      // If layers are provided, look for those layers in allLayers
      const filteredLayers = allLayers?.filter((layer) =>
        layers.some((l) => l.id === layer.id)
      );
      setLayers(filteredLayers ?? []);
    } else {
      // If layers are not provided, filter layers based on the category
      const categoryLayers =
        allLayers?.filter(
          (layer) => getCategoryFromLayerName(layer.name) === category
        ) ?? [];
      setLayers(categoryLayers);
    }

    setPage("masterplan");
  };

  /** Categorizes attribute names for polygon layers into discrete (string) and continuous (numerical) */
  const categorizeAttributes = (
    layer: any
  ): { discrete: string[]; continuous: string[] } => {
    // The dmp-2 block plan only carries CAD/geometry fields, so offering them
    // as styling options in the Masterplan menu would be meaningless.
    if (layerStage === STAGE_DMP2) {
      return { discrete: [], continuous: [] };
    }

    if (
      layer.geometryType !== "esriGeometryPolygon" &&
      layer.geometryType !== "esriGeometryPolyline" &&
      layer.geometryType !== "esriGeometryPoint"
    ) {
      return { discrete: [], continuous: [] };
    }

    const attributesCategorized = {
      discrete: new Set<string>(),
      continuous: new Set<string>(),
    };

    layer.features.forEach((feature: any) => {
      Object.entries(feature.attributes).forEach(
        ([attributeName, attributeValue]) => {
          // Convert attributeName to lowercase for a case-insensitive comparison
          const attributeNameLower = attributeName.toLowerCase();
          // Check if attributeNameLower is in the exclude list
          if (!EXCLUDED_ATTRIBUTES.includes(attributeNameLower)) {
            // Check if value is a string (discrete)
            if (typeof attributeValue === "string") {
              attributesCategorized.discrete.add(attributeName);
            }
            // Check if value is a number (continuous)
            else if (typeof attributeValue === "number") {
              attributesCategorized.continuous.add(attributeName);
            }
          }
        }
      );
    });

    return {
      discrete: Array.from(attributesCategorized.discrete),
      continuous: Array.from(attributesCategorized.continuous),
    };
  };

  // Define the layers for Public Transport Network
  const publicTransportLayers = [
    "pt_network_point",
    "pt_network_lines",
    "PT_Metro_Route",
    "PT_Metro_Station",
    "PT_Tram_Route",
    "PT_Tram_Station",
  ];
  // Filter layers according to Public Transport Network
  const filteredPublicTransportLayers = allLayers?.filter((layer) => 
    publicTransportLayers.includes(layer.name));

  // Filter layers that belong to Public Transport Network
  const publicTransportItems = [
    // Add the "Default" option to show all Public Transport layers
    {
      id: "all",
      name: "All",
      action: () => {
        setLayers(filteredPublicTransportLayers);
        setPage("masterplan");
      },
    },
    ...(filteredPublicTransportLayers?.map((layer) => {
        const { discrete, continuous } = categorizeAttributes(layer);

        // Merge discrete and continuous attributes for dropdown
        const attributeItems = [
          ...discrete.map((attributeName) => ({
            id: `${layer.id}-${attributeName}`,
            name: getReadableName(attributeName),
            action: () => navigateToLayer(layer, attributeName, "discrete"),
          })),
          ...continuous.map((attributeName) => ({
            id: `${layer.id}-${attributeName}`,
            name: getReadableName(attributeName),
            action: () => navigateToLayer(layer, attributeName, "continuous"),
          })),
        ];

        // Dynamically generate dropdownItems based on attributes and include a "default" item
        const dropdownItems =
          attributeItems.length > 0
            ? [
              {
                id: `${layer.id}-default`,
                name: "Default",
                action: () => navigateToLayer(layer),
              },
              ...attributeItems,
            ]
            : undefined; 

        return {
          id: layer.id,
          name: getReadableName(layer.name),
          action: () => navigateToLayer(layer),
          dropdownItems,
          isDropdown: !!dropdownItems,
        };
      })) || [],
  ];

  // Filter out Public Transport Network layers from masterplanItems
  const masterplanItems = allLayers
    ?.filter(
      (layer) =>
        !layer.name.startsWith("infra_") &&
        !layer.name.startsWith("landscape") &&
        !publicTransportLayers.includes(layer.name)
    )
    .map((layer) => {
      const { discrete, continuous } = categorizeAttributes(layer);

      // Merge discrete and continuous attributes for dropdown
      const attributeItems = [
        ...discrete.map((attributeName) => ({
          id: `${layer.id}-${attributeName}`,
          name: getReadableName(attributeName),
          action: () => navigateToLayer(layer, attributeName, "discrete"),
        })),
        ...continuous.map((attributeName) => ({
          id: `${layer.id}-${attributeName}`,
          name: getReadableName(attributeName),
          action: () => navigateToLayer(layer, attributeName, "continuous"),
        })),
      ];

      // Dynamically generate dropdownItems based on attributes and include a "default" item
      const dropdownItems =
        attributeItems.length > 0
          ? [
            {
              id: `${layer.id}-default`,
              name: "Default",
              action: () => navigateToLayer(layer),
            },
            ...attributeItems,
          ]
          : undefined; // Don't add dropdownItems if no attributes are available

      return {
        id: layer.id,
        name: getReadableName(layer.name),
        action: () => navigateToLayer(layer),
        dropdownItems,
        isDropdown: !!dropdownItems,
      };
    });

  // Conditionally add the Public Transport Network item to the masterplanItems
  if (filteredPublicTransportLayers.length > 0) {
    masterplanItems.push({
      id: "public-transport-network",
      name: "Public Transport Network",
      action: () => {},
      dropdownItems: publicTransportItems,
      isDropdown: true,
    });
  }

  // Utility function to get category from layer name
  const getCategoryFromLayerName = (layerName: string) => {
    const parts = layerName.split("_");
    return parts.length > 1 ? parts[1] + " " + parts[2] : null;
  };

  // Utility function to get the readable name for infrastructure items
  const getReadableNameInfra = (name: string) => {
    return name
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Defining the LayerType
  type LayerType = {
    id: string;
    name: string;
    features: {
      attributes: { [key: string]: any };
    }[];
  };

  // Filtering layers starting with "infra_"
  const infrastructureLayers = allLayers?.filter((layer: LayerType) =>
    layer.name.startsWith("infra_")
  );

  // Categorizing layers by their category names
  type CategorizedLayersType = { [key: string]: LayerType[] };

  // Categorizing layers by their category names
  const categorizedLayers = infrastructureLayers?.reduce(
    (acc: CategorizedLayersType, layer: LayerType) => {
      const category = getCategoryFromLayerName(layer.name);
      if (category) {
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(layer);
      }
      return acc;
    },
    {}
  );

  // Process infrastructure layers to generate dropdown items
  const infrastructureItems = Object.entries(categorizedLayers ?? {}).map(
    ([category, layers]) => {
      const categoryItems = (layers as LayerType[]).map((layer: LayerType) => {
        const attributesCategorized = {
          discrete: new Set<string>(),
          continuous: new Set<string>(),
        };

        layer.features.forEach((feature) => {
          for (const attributeName in feature.attributes) {
            if (
              Object.prototype.hasOwnProperty.call(
                feature.attributes,
                attributeName
              )
            ) {
              const attributeValue = feature.attributes[attributeName];
              const attributeNameLower = attributeName.toLowerCase();
              if (!EXCLUDED_ATTRIBUTES.includes(attributeNameLower)) {
                if (typeof attributeValue === "string") {
                  attributesCategorized.discrete.add(attributeName);
                } else if (typeof attributeValue === "number") {
                  attributesCategorized.continuous.add(attributeName);
                }
              }
            }
          }
        });

        const discreteItems = Array.from(attributesCategorized.discrete).map(
          (attributeName) => ({
            id: `${layer.id}-${attributeName}-discrete`,
            name: getReadableName(attributeName),
            action: () => navigateToLayer(layer, attributeName, "discrete"),
          })
        );

        const continuousItems = Array.from(
          attributesCategorized.continuous
        ).map((attributeName) => ({
          id: `${layer.id}-${attributeName}-continuous`,
          name: getReadableName(attributeName),
          action: () => navigateToLayer(layer, attributeName, "continuous"),
        }));

        const attributeItems = [...discreteItems, ...continuousItems];

        const dropdownItems =
          attributeItems.length > 0
            ? [
              {
                id: `${layer.id}-default`,
                name: "Default",
                action: () => navigateToLayer(layer),
              },
              ...attributeItems,
            ]
            : undefined;

        return {
          id: layer.id,
          name: getReadableNameInfra(layer.name.split("_").slice(1).join("_")),
          action: () => navigateToLayer(layer),
          dropdownItems,
          isDropdown: !!dropdownItems,
        };
      });

      // Include the "Default" option for showing all layers of the category
      categoryItems.unshift({
        id: `${category}-default`,
        name: "Default",
        action: () => navigateToCategoryLayers(category),
        dropdownItems: undefined,
        isDropdown: false,
      });

      return {
        id: `infra_${category}`,
        name: getReadableNameInfra(category),
        action: undefined,
        dropdownItems: categoryItems,
        isDropdown: !!categoryItems,
      };
    }
  );

  // Filter layers starting with "landscape_"
  const landscapeLayers = allLayers?.filter((layer) =>
    layer.name.startsWith("landscape_")
  );

  // Process landscape layers to generate dropdown items
  const landscapeItems = [
    // Add the "Default" option to show all landscape layers
    {
      id: "landscape_all",
      name: "Landscape",
      action: () => {
        setLayers(landscapeLayers);
        setPage("masterplan");
      },
    },
    ...landscapeLayers?.map((layer) => {
      const { discrete, continuous } = categorizeAttributes(layer);

      // Merge discrete and continuous attributes for dropdown
      const attributeItems = [
        ...discrete.map((attributeName) => ({
          id: `${layer.id}-${attributeName}`,
          name: getReadableName(attributeName),
          action: () => navigateToLayer(layer, attributeName, "discrete"),
        })),
        ...continuous.map((attributeName) => ({
          id: `${layer.id}-${attributeName}`,
          name: getReadableName(attributeName),
          action: () => navigateToLayer(layer, attributeName, "continuous"),
        })),
      ];

      // Generate attribute items for each attribute in the layer
      const excludedAttributes = new Set(EXCLUDED_ATTRIBUTES);
      const attributeItemsDetail = Object.keys(layer.features[0].attributes)
        .filter((attributeName) => !excludedAttributes.has(attributeName))
        .map((attributeName) => ({
          id: `${layer.id}-${attributeName}`,
          name: getReadableName(attributeName),
          action: () => navigateToLayer(layer, attributeName, "continuous"),
        }));

      // Dynamically generate dropdownItems based on attributes and include a "default" item
      const dropdownItems =
        attributeItems.length > 0
          ? [
            {
              id: `${layer.id}-default`,
              name: "Default",
              action: () => navigateToLayer(layer),
            },
            ...attributeItems,
          ]
          : attributeItemsDetail.length > 0
            ? [
              {
                id: `${layer.id}-default`,
                name: "Default",
                action: () => navigateToLayer(layer),
              },
              ...attributeItemsDetail,
            ]
            : undefined;

      // Process layer name for display in dropdown with removing the "landscape_"
      const layerName = layer.name.substring(10).replace(/_/g, " ");

      return {
        id: layer.id,
        name: getReadableName(layerName),
        action: () => navigateToLayer(layer),
        dropdownItems,
        isDropdown: !!dropdownItems,
      };
    })
  ];


  // Rendering Infrastructure only for d1-dmp stage
  const hasInfrastructureItems = layerStage === "d1-dmp";

  // Include landscape nav item except when layerStage is d1-cmp, or dmp-2
  // which only publishes a block plan and would render an empty dropdown
  const includeLandscapeItem =
    layerStage !== "d1-cmp" && layerStage !== STAGE_DMP2;

  const navItems: Array<NavItemType> = [
    {
      id: "website",
      name: "Website",
      action: () => {
        window.open("https://www.yangitoshkent.uz/", "_blank");
      },
    },
    {
      id: "masterplan",
      name: "Masterplan",
      isDropdown: masterplanItems?.length > 0,
      dropdownItems: masterplanItems,
    },
    ...(hasInfrastructureItems
      ? [
        {
          id: "infrastructure",
          name: "Infrastructure",
          isDropdown: infrastructureItems?.length > 0,
          dropdownItems: infrastructureItems,
          private: true,
        },
      ]
      : []),
    ...(includeLandscapeItem
      ? [
        {
          id: "landscape",
          name: "Landscape",
          isDropdown: landscapeItems?.length > 0,
          dropdownItems: landscapeItems,
        },
      ]
      : []),
    { id: "guidelines", name: "Guidelines", private: true },
    { id: "visualisation", name: "Visualisation" },
    { id: "experience", name: "Experience", private: true },
  ];

  // Determine mobile menu contents
  const mobileMenuContents = mobileMenuView ? mobileMenuView : navItems;

  // Check if used is on any logged out pages
  const loggedOut =
    location.pathname === "/" ||
    location.pathname === "/register" ||
    location.pathname === "/forgot-password";

  return (
    <>
      <div
        style={{
          width: "100vw",
          height: HEADER_HEIGHT,
          backgroundColor: "#171717",
          display: "flex",
          justifyContent: "space-between",
          position: 'sticky',
          zIndex: 999
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 30,
            marginLeft: 30,
            height: "inherit",
            fontSize: 18,
          }}
        >
          {/* Logo and home button */}
          <img
            src={yt_logo_white}
            alt="Yangi Toshkent"
            width={200}
            onClick={() => setPage("home")}
            style={{ cursor: "pointer", height: "70%" }}
          />
        </div>

        {!minimalHeader && !loggedOut && !checkingStatus && (
          <>
            {!isMobile && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "0px 30px",
                    height: "inherit",
                    gap: 30,
                    width: "100%",
                    overflowX: "hidden",
                  }}
                >
                  {navItems.map((item: NavItemType) =>
                    !item.private ||
                      (item.private && userRole === "private") ? (
                      <NavItem
                        key={item.id}
                        {...item}
                        currentPage={currentPage}
                        action={() =>
                          item.action ? item.action() : setPage(item.id)
                        }
                        openDropdown={openDropdown}
                        setOpenDropdown={setOpenDropdown}
                      />
                    ) : null
                  )}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: 30,
                    height: "inherit",
                    gap: 15,
                  }}
                >
                  {/* <NavItem
                    id={"uzbek"}
                    name={"Oʻzbekcha"}
                    currentPage={currentPage}
                    action={() => console.log("Translate")}
                  /> */}

                  <div
                    title={hoverMessage}
                    onClick={() => setPage("Profile")}
                    style={{ color: "white", cursor: "pointer" }}
                  >
                    <FaRegCircleUser style={{ fontSize: 28 }} />
                  </div>
                  <button
                    type="submit"
                    onClick={logout}
                    className="RoundButton"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}

            {isMobile && (
              <>
                <>
                  <div
                    style={{
                      padding: 20,
                      display: "flex",
                      alignItems: "center",
                      gap: 20,
                    }}
                  >
                    <div
                      onClick={() => {
                        setMobileMenuOpen(!mobileMenuOpen);
                        if (mobileMenuView) setMobileMenuView(null);
                      }}
                    >
                      {mobileMenuOpen ? (
                        <ImCross color="white" style={{ fontSize: 20 }} />
                      ) : (
                        <RxHamburgerMenu
                          color="white"
                          style={{ fontSize: 24 }}
                        />
                      )}
                    </div>

                    <div
                      title={hoverMessage}
                      style={{ color: "white", cursor: "pointer" }}
                      onClick={() => setPage("Profile")}
                    >
                      <FaRegCircleUser style={{ fontSize: 20 }} />
                    </div>
                  </div>
                </>
                {mobileMenuOpen && (
                  <>
                    <div
                      style={{
                        position: "absolute",
                        top: HEADER_HEIGHT,
                        zIndex: 999,
                        width: "100%",
                        backgroundColor: "#171717",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 15,
                        padding: "15px 0px",
                        borderTop: "1px solid white",
                      }}
                    >
                      <>
                        {mobileMenuView && (
                          <button
                            className="RoundButton"
                            style={{
                              padding: "0 20px",
                              fontSize: 12,
                              height: 30,
                            }}
                            onClick={() => setMobileMenuView(null)}
                          >
                            Back
                          </button>
                        )}

                        {mobileMenuContents.map((item: NavItemType) => (
                          (!item.private || (item.private && userRole === "private")) && (
                          <NavItem
                            key={item.id}
                            {...item}
                            currentPage={currentPage}
                            overrideDropdown={true}
                            action={() => {
                              if (item.isDropdown && item.dropdownItems) {
                                setMobileMenuView(item.dropdownItems);
                              } else {
                                if (item.action) {
                                  item.action();
                                } else {
                                  setPage(item.id);
                                }
                                setMobileMenuOpen(false);
                              }
                            }}
                          />
                        )))}

                        {!mobileMenuView && (
                          <NavItem
                            id={"log-out"}
                            name={"Log Out"}
                            action={() => {
                              logout();
                              setMobileMenuOpen(false);
                            }}
                            currentPage={currentPage}
                          />
                        )}
                      </>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Header;

interface NavItemProps extends NavItemType {
  currentPage: string;
  overrideDropdown?: boolean;
  action: () => void;
  openDropdown?: string | null;
  setOpenDropdown?: (id: string | null) => void;
}

/**
 * A component that renders a navigation item.
 * The item can be a simple link or a dropdown menu with sub-items.
 *
 * @param {Object} props - The component props.
 * @param {string} props.id - The ID of the navigation item.
 * @param {string} props.name - The display name of the navigation item.
 * @param {boolean} [props.private] - Whether the navigation item is private and requires special access.
 * @param {boolean} [props.isDropdown] - Whether the navigation item has a dropdown menu.
 * @param {NavItemType[]} [props.dropdownItems] - The items in the dropdown menu.
 * @param {boolean} [props.overrideDropdown] - Whether to override the default dropdown behavior.
 * @param {string} props.currentPage - The ID of the current page.
 * @param {() => void} props.action - The action to perform when the item is clicked.
 * @param {string | null} [props.openDropdown] - The ID of the currently open dropdown.
 * @param {(id: string | null) => void} [props.setOpenDropdown] - Function to set the open dropdown.
 *
 * @returns {JSX.Element} The rendered NavItem component.
 */
const NavItem: React.FC<NavItemProps> = ({
  id,
  name,
  action,
  currentPage,
  isDropdown = false,
  dropdownItems = [],
  overrideDropdown = false,
  openDropdown,
  setOpenDropdown,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const showDropdown = openDropdown === id;

  return (
    <div
      ref={dropdownRef}
      onClick={() => {
        if (isDropdown && !overrideDropdown) {
          if (setOpenDropdown) setOpenDropdown(showDropdown ? null : id);
        } else {
          action();
          if (setOpenDropdown) setOpenDropdown(null);
        }
      }}
      style={{
        color: currentPage === id || showDropdown ? "#4a74b8" : "white",
        cursor: "pointer",
        userSelect: "none",
        display: "flex",
      }}
    >
      {name}
      {isDropdown && (
        <>
          <div
            style={{
              marginLeft: 10,
              transform: showDropdown ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▼
          </div>
          {showDropdown && (
            <div
              style={{
                position: "absolute",
                top: HEADER_HEIGHT + 10,
                zIndex: 999,
              }}
            >
              <Dropdown
                toggleDropdown={() => {
                  if (setOpenDropdown) setOpenDropdown(null);
                }}
                items={dropdownItems}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

interface DropdownProps {
  items: NavItemType[];
  toggleDropdown: () => void;
}

/**
 * A component that renders a dropdown menu with nested sub-items.
 *
 * @param {Object} props - The component props.
 * @param {NavItemType[]} props.items - The items in the dropdown menu.
 * @param {() => void} props.toggleDropdown - Function to toggle the dropdown menu.
 *
 * @returns {JSX.Element} The rendered Dropdown component.
 */
const Dropdown: React.FC<DropdownProps> = ({ items, toggleDropdown }) => {
  const [activeDropdowns, setActiveDropdowns] = useState<{
    [key: string]: string;
  }>({});
  const [dropdownPositions, setDropdownPositions] = useState<{
    [key: string]: any;
  }>({});

  /**
   * Handles the click event for a dropdown item.
   * If the item has secondary items, it toggles the secondary dropdown.
   *
   * @param {NavItemType} item - The dropdown item.
   * @param {React.MouseEvent} e - The click event.
   */
  const handleItemClick = (item: NavItemType, parentId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const maxHeight = window.innerHeight * 0.5; // half the screen
    const bottomSpace = window.innerHeight - rect.bottom;

    const shouldUseBottom = maxHeight > bottomSpace && rect.top > bottomSpace;
    const newPosition = {
      top: shouldUseBottom ? undefined : rect.top + window.scrollY,
      bottom: shouldUseBottom
        ? window.innerHeight - rect.top - window.scrollY
        : undefined,
      left: rect.right + 30,
    };

    if (item.dropdownItems) {
      const isActive = activeDropdowns[parentId] === item.id;
      setDropdownPositions({ ...dropdownPositions, [item.id]: newPosition });

      if (isActive) {
        const { [parentId]: _, ...rest } = activeDropdowns;
        setActiveDropdowns(rest);
      } else {
        setActiveDropdowns({ ...activeDropdowns, [parentId]: item.id });
      }
    } else {
      // No secondary items, perform the item's action
      if (item.action) item.action();
      toggleDropdown();
    }
  };



  const renderDropdownItems = (items: NavItemType[], parentId: string) => {
    return items.map((item) => (
      <div
        key={item.id}
        onClick={(e) => handleItemClick(item, parentId, e)}
        style={{
          borderBottom: "1px solid white",
          margin: "20px 0px",
          color: activeDropdowns[parentId] === item.id ? "#4a74b8" : "white",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {item.name}
        {item.dropdownItems && (
          <span style={{ marginLeft: 20, cursor: "pointer" }}>▶</span>
        )}
        {activeDropdowns[parentId] === item.id &&
          item.dropdownItems &&
          ReactDOM.createPortal(
            <div
              style={{
                position: "absolute",
                top: `${dropdownPositions[item.id]?.top}px`,
                bottom: `${dropdownPositions[item.id]?.bottom}px`,
                left: `${dropdownPositions[item.id]?.left}px`,
                backgroundColor: "rgba(0,0,0,0.7)",
                borderRadius: 10,
                padding: "10px 20px",
                whiteSpace: "nowrap",
                maxHeight: "35vh",
                overflowY: "auto",
                cursor: 'pointer'
              }}
            >
              {renderDropdownItems(item.dropdownItems, item.id)}
            </div>,
            document.body
          )}
      </div>
    ));
  };


  return (
    <div
      style={{
        backgroundColor: "rgba(0,0,0,0.7)",
        borderRadius: 10,
        padding: "10px 20px",
        position: "relative",
        maxHeight: "70vh",
        overflowY: "auto",
      }}
    >
      {renderDropdownItems(items, "root")}
    </div>
  );
};
